// src/features/dashboard/components/EmployeeProductivity/ProductivityTrendChart.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

import type {
  ProductivityTrendItem,
} from '../../types/Employee/employeeProductivity.types';

interface Props {
  data: ProductivityTrendItem[];
  todayProductivity: number;
}

/* =========================================================
   FORMAT DAY LABEL
========================================================= */

/*
 * Converts:
 *
 * Mon10  -> Mon 10
 * Tue11  -> Tue 11
 * Wed12  -> Wed 12
 *
 * If the value already contains a space:
 *
 * Mon 10 -> Mon 10
 *
 * Any other value is returned unchanged.
 */
const formatDayLabel = (
  value: string | number,
): string => {
  const text =
    String(value).trim();

  /*
   * Match:
   *
   * Mon10
   * Tue11
   * Wed12
   *
   * Also supports:
   *
   * Mon 10
   * Tue 11
   */
  const match =
    text.match(
      /^([A-Za-z]{3})\s*(\d{1,2})$/,
    );

  if (match) {
    return `${match[1]} ${match[2]}`;
  }

  return text;
};

/* =========================================================
   TOOLTIP
========================================================= */

const CustomTooltip = ({
  active,
  payload,
  label,
  target,
}: any) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const productivity =
    payload.find(
      (p: any) =>
        p.dataKey === 'productivity',
    )?.value;

  /*
   * Make sure tooltip day also uses:
   *
   * Mon 10
   * Tue 11
   */
  const formattedLabel =
    formatDayLabel(label ?? '');

  return (
    <div
      className="
        relative
        w-[145px]
        overflow-visible
        rounded-md
        border
        border-[#D0D5DD]
        bg-white
        shadow-[0_12px_24px_rgba(16,24,40,0.18)]
      "
    >
      {/* Header */}

      <div
        className="
          border-b
          border-[#D0D5DD]
          bg-[#ECEFF3]
          px-2.5
          py-2
        "
      >
        <p
          className="
            text-[12px]
            font-medium
            leading-none
            text-[#101828]
          "
        >
          {formattedLabel}
        </p>
      </div>

      {/* Body */}

      <div
        className="
          space-y-4
          px-4
          py-4
        "
      >
        {/* Productivity */}

        <div
          className="
            flex
            items-center
            justify-between
          "
        >
          <div
            className="
              flex
              items-center
              gap-3
            "
          >
            <span
              className="
                h-3.5
                w-3.5
                rounded-full
                bg-[#22C55E]
              "
            />

            <span
              className="
                text-[10px]
                font-normal
                text-[#101828]
              "
            >
              Productivity:
            </span>
          </div>

          <span
            className="
              text-[10px]
              font-semibold
              text-[#101828]
            "
          >
            {productivity}%
          </span>
        </div>

        {/* Target */}

        <div
          className="
            flex
            items-center
            justify-between
          "
        >
          <div
            className="
              flex
              items-center
              gap-3
            "
          >
            <span
              className="
                h-3.5
                w-3.5
                rounded-full
                bg-[#98A2B3]
              "
            />

            <span
              className="
                text-[10px]
                font-normal
                text-[#101828]
              "
            >
              Target:
            </span>
          </div>

          <span
            className="
              text-[10px]
              font-semibold
              text-[#101828]
            "
          >
            {target}%
          </span>
        </div>
      </div>

      {/* Arrow */}

      <div
        className="
          absolute
          left-1/2
          top-full
          h-4
          w-4
          -translate-x-1/2
          -translate-y-2
          rotate-45
          border-r
          border-b
          border-[#D0D5DD]
          bg-white
        "
      />
    </div>
  );
};

/* =========================================================
   CUSTOM X-AXIS TICK
========================================================= */

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string | number;
    index?: number;
  };
  activeIndex?: number | null;
}

const CustomXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  activeIndex,
}: CustomXAxisTickProps) => {
  if (!payload) {
    return null;
  }

  /*
   * Original value from API/chart data.
   *
   * Examples:
   *
   * Mon10
   * Tue11
   * Wed12
   */
  const rawValue =
    payload.value ?? '';

  /*
   * Always convert to:
   *
   * Mon 10
   * Tue 11
   * Wed 12
   */
  const value =
    formatDayLabel(rawValue);

  const index =
    payload.index ?? -1;

  const isActive =
    activeIndex !== null &&
    activeIndex !== undefined &&
    index === activeIndex;

  return (
    <g
      transform={`translate(${x},${y})`}
      pointerEvents="none"
      style={{
        overflow: 'visible',
      }}
    >
      {isActive ? (
        <>
          {/* =========================================================
              POINTER
          ========================================================= */}

          <path
            d="M -8 6 L 0 -2 L 8 6 Z"
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* =========================================================
              SEAMLESS POINTER CONNECTION
          ========================================================= */}

          <rect
            x="-9"
            y="4"
            width="18"
            height="5"
            fill="#FFFFFF"
          />

          {/* =========================================================
              SELECTED DAY BOX
          ========================================================= */}

          <rect
            x="-30"
            y="6"
            width="60"
            height="38"
            rx="2"
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          {/* =========================================================
              SELECTED DAY
          ========================================================= */}

          <text
            x="0"
            y="27"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#344054"
            fontSize={10}
            fontWeight={400}
          >
            {value}
          </text>
        </>
      ) : (
        <>
          {/* =========================================================
              NORMAL DAY LABEL
          ========================================================= */}

          <text
            x="0"
            y="20"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#344054"
            fontSize={12}
            fontWeight={400}
          >
            {value}
          </text>
        </>
      )}
    </g>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const ProductivityTrendChart = ({
  data,
}: Props) => {
  /* =======================================================
     ZOOM
  ======================================================= */

  const [zoom, setZoom] =
    useState<number>(1);

  /* =======================================================
     ACTIVE SERIES
  ======================================================= */

  const [
    activeSeries,
    setActiveSeries,
  ] = useState<string | null>(null);

  /* =======================================================
     ACTIVE X-AXIS POINT
  ======================================================= */

  const [
    activeXAxisIndex,
    setActiveXAxisIndex,
  ] = useState<number | null>(null);

  /* =======================================================
     CHART REF
  ======================================================= */

  const chartRef =
    useRef<HTMLDivElement | null>(null);

  /* =======================================================
     VISIBLE DATA
  ======================================================= */

  const visibleData =
    useMemo(() => {
      if (
        !Array.isArray(data) ||
        data.length === 0
      ) {
        return [];
      }

      /*
       * 1x = show all data.
       */
      if (zoom === 1) {
        return data;
      }

      /*
       * Reduce visible points
       * when zooming in.
       */
      const visibleCount =
        Math.max(
          3,
          Math.round(
            data.length / zoom,
          ),
        );

      /*
       * Show latest data.
       */
      return data.slice(
        Math.max(
          0,
          data.length -
            visibleCount,
        ),
      );
    }, [data, zoom]);

  /* =======================================================
     RESET ACTIVE POINT
  ======================================================= */

  useEffect(() => {
    setActiveXAxisIndex(null);
  }, [data]);

  /* =======================================================
     WHEEL ZOOM

     IMPORTANT:
     We use a native wheel listener with
     passive:false.

     This allows preventDefault()
     to stop PAGE scrolling.
  ======================================================= */

  useEffect(() => {
    const chartElement =
      chartRef.current;

    if (!chartElement) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      /*
       * Stop browser page scrolling.
       */
      event.preventDefault();

      /*
       * Stop event bubbling.
       */
      event.stopPropagation();

      /*
       * Scroll UP = zoom IN
       * Scroll DOWN = zoom OUT
       */
      setZoom((currentZoom) => {
        let nextZoom =
          currentZoom;

        if (
          event.deltaY < 0
        ) {
          nextZoom =
            currentZoom + 0.25;
        }

        if (
          event.deltaY > 0
        ) {
          nextZoom =
            currentZoom - 0.25;
        }

        /*
         * Minimum = 1x
         * Maximum = 3x
         */
        nextZoom = Math.max(
          1,
          Math.min(
            3,
            nextZoom,
          ),
        );

        return Number(
          nextZoom.toFixed(2),
        );
      });
    };

    /*
     * passive:false is VERY IMPORTANT.
     */
    chartElement.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      chartElement.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, []);

  /* =======================================================
     PRODUCTIVITY AVERAGE
  ======================================================= */

  const dailyProductivity =
    data.length > 0
      ? Math.round(
          data.reduce(
            (sum, item) =>
              sum +
              item.productivity,
            0,
          ) / data.length,
        )
      : 0;

  /* =======================================================
     TARGET
  ======================================================= */

  const target =
    data.length > 0
      ? data[0].target
      : 80;

  /* =======================================================
     LEGEND
  ======================================================= */

  const CustomLegend = () => (
    <div
      className="
        mb-5
        flex
        justify-center
        gap-8
      "
    >
      {/* Productivity */}

      <div
        className="
          flex
          cursor-pointer
          items-center
          gap-2.5
        "
        onMouseEnter={() =>
          setActiveSeries(
            'productivity',
          )
        }
        onMouseLeave={() =>
          setActiveSeries(null)
        }
      >
        <span
          className="
            h-4
            w-4
            rounded-full
            transition-all
          "
          style={{
            backgroundColor:
              '#22C55E',
            opacity:
              activeSeries &&
              activeSeries !==
                'productivity'
                ? 0.35
                : 1,
          }}
        />

        <span
          className={`
            text-[11px]
            transition-all
            ${
              activeSeries ===
              'productivity'
                ? 'font-semibold text-[#101828]'
                : 'text-[#344054]'
            }
          `}
        >
          Productivity
        </span>
      </div>

      {/* Target */}

      <div
        className="
          flex
          cursor-pointer
          items-center
          gap-2.5
        "
        onMouseEnter={() =>
          setActiveSeries(
            'target',
          )
        }
        onMouseLeave={() =>
          setActiveSeries(null)
        }
      >
        <span
          className="
            h-4
            w-4
            rounded-full
            transition-all
          "
          style={{
            backgroundColor:
              '#98A2B3',
            opacity:
              activeSeries &&
              activeSeries !==
                'target'
                ? 0.35
                : 1,
          }}
        />

        <span
          className={`
            text-[11px]
            transition-all
            ${
              activeSeries ===
              'target'
                ? 'font-semibold text-[#101828]'
                : 'text-[#344054]'
            }
          `}
        >
          Target
        </span>
      </div>
    </div>
  );

  /* =======================================================
     Y AXIS
  ======================================================= */

  const values = [
    ...data.map(
      (item) =>
        item.productivity,
    ),
    target,
  ];

  const minValue =
    values.length > 0
      ? Math.min(...values)
      : 0;

  const maxValue =
    values.length > 0
      ? Math.max(...values)
      : 100;

  const yMin = Math.max(
    0,
    Math.floor(
      (minValue - 5) / 5,
    ) * 5,
  );

  const yMax = Math.min(
    100,
    Math.ceil(
      (maxValue + 5) / 5,
    ) * 5,
  );

  const ticks: number[] = [];

  for (
    let value = yMin;
    value <= yMax;
    value += 5
  ) {
    ticks.push(value);
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="
        rounded-2xl
        border
        border-[#D0D5DD]
        bg-white
        p-5
      "
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <div
        className="
          mb-5
          flex
          items-center
        "
      >
        <h3
          className="
            text-[16px]
            font-semibold
            text-[#002B5B]
          "
        >
          WTD Productivity Trend
        </h3>

        <div
          className="
            ml-3
            rounded-full
            bg-[#EEF4FF]
            px-4
            py-1.5
            text-[11px]
            font-medium
            text-[#2563EB]
          "
        >
          Daily Productivity{' '}
          {dailyProductivity}% · Target{' '}
          {target}%
        </div>
      </div>

      {/* =================================================
          LEGEND
      ================================================= */}

      <CustomLegend />

      {/* =================================================
          CHART CONTAINER

          This is the ONLY area where wheel
          scrolling is captured.
      ================================================= */}

      <div
        ref={chartRef}
        className="
          relative
          h-[230px]
          w-full
          overflow-hidden
          select-none
        "
        style={{
          overscrollBehavior:
            'contain',
        }}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <LineChart
            data={visibleData}
            margin={{
              top: 8,
              right: 24,
              left: 4,
              bottom: 18,
            }}
            onMouseMove={(
              state: any,
            ) => {
              const index =
                state?.activeTooltipIndex;

              if (
                index !== undefined &&
                index !== null &&
                Number.isFinite(
                  Number(index),
                )
              ) {
                setActiveXAxisIndex(
                  Number(index),
                );
              }
            }}
            onMouseLeave={() => {
              setActiveXAxisIndex(
                null,
              );
            }}
          >
            {/* =================================================
                GRID
            ================================================= */}

            <CartesianGrid
              vertical={false}
              stroke="#EAECF0"
              strokeDasharray="0"
            />

            {/* =================================================
                X AXIS
            ================================================= */}

            <XAxis
              dataKey="day"
              axisLine={{
                stroke:
                  '#D0D5DD',
              }}
              tickLine={false}
              tickMargin={0}
              interval={0}
              height={58}
              padding={{
                left: 8,
                right: 8,
              }}
              tick={
                <CustomXAxisTick
                  activeIndex={
                    activeXAxisIndex
                  }
                />
              }
            />

            {/* =================================================
                Y AXIS
            ================================================= */}

            <YAxis
              domain={[
                yMin,
                yMax,
              ]}
              ticks={ticks}
              axisLine={false}
              tickLine={false}
              width={40}
              tickFormatter={(
                value,
              ) =>
                `${value}%`
              }
              tick={{
                fill: '#475467',
                fontSize: 12,
              }}
            />

            {/* =================================================
                TOOLTIP
            ================================================= */}

            <Tooltip
              content={
                <CustomTooltip
                  target={target}
                />
              }
              offset={18}
              cursor={{
                stroke:
                  '#98A2B3',
                strokeDasharray:
                  '4 4',
                strokeWidth: 1.5,
              }}
              wrapperStyle={{
                outline: 'none',
                zIndex: 1000,
              }}
            />

            {/* =================================================
                TARGET REFERENCE LINE
            ================================================= */}

            <ReferenceLine
              y={target}
              stroke="#98A2B3"
              strokeDasharray="5 5"
              strokeWidth={
                activeSeries ===
                'target'
                  ? 2.5
                  : 1.5
              }
              strokeOpacity={
                activeSeries &&
                activeSeries !==
                  'target'
                  ? 0.25
                  : 1
              }
            />

            {/* =================================================
                PRODUCTIVITY LINE
            ================================================= */}

            <Line
              type="monotone"
              dataKey="productivity"
              stroke="#22C55E"
              strokeWidth={
                activeSeries ===
                'productivity'
                  ? 4
                  : 3
              }
              strokeOpacity={
                activeSeries &&
                activeSeries !==
                  'productivity'
                  ? 0.25
                  : 1
              }
              connectNulls
              onMouseEnter={() =>
                setActiveSeries(
                  'productivity',
                )
              }
              onMouseLeave={() =>
                setActiveSeries(null)
              }
              dot={{
                r: 4,
                fill: '#22C55E',
                stroke: '#FFFFFF',
                strokeWidth: 2,
              }}
              activeDot={{
                r: 7,
                fill: '#22C55E',
                stroke: '#FFFFFF',
                strokeWidth: 3,
              }}
              animationDuration={300}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* =================================================
            ZOOM INDICATOR
        ================================================= */}

        <div
          className="
            pointer-events-none
            absolute
            right-2
            top-2
            rounded-full
            bg-[#F0FDF4]
            px-2
            py-1
            text-[10px]
            font-medium
            text-[#16A34A]
          "
        >
          {zoom.toFixed(2)}x
        </div>
      </div>
    </div>
  );
};

export default ProductivityTrendChart;